/**
 * Auth Signup Controller (EPIC-002 / 2.4)
 *
 * Submits the email / password / termsAccepted form to POST /auth/signup.
 * On 201 we redirect to /inscription-succes.html?email=<URLencoded>. The backend
 * answers 201 uniformly for the nominal case and the duplicate-email case
 * (D8 — anti-enumeration), so the client logic does not branch on the
 * outcome.
 *
 * On 422 the backend returns the field-level violations under
 * `error.details.fields` (cf. SignupService::validateInputs) — we surface
 * them inline below the matching <input> via aria-describedby + role=alert
 * paragraphs already wired in the HTML. No HTML inline in JS (CLAUDE.md
 * rule 8) — we only set `textContent` on the existing nodes.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "../services/api.js";
import {
  validatePassword,
  formatViolations,
  checkRules,
  RULE_LABELS,
} from "../utils/password_policy.js";

const TERMS_VERSION = "2026-05";

const VIOLATION_LABELS = {
  // email
  invalid_format: "Adresse email invalide.",
  too_long: "Adresse email trop longue (254 caractères max).",
  // termsAccepted
  must_be_true: "Vous devez accepter les CGU pour continuer.",
  // termsVersion (defensive — should never reach the user since the field
  // is hidden client-side)
  unsupported_version: "Version des CGU obsolète. Rechargez la page.",
};

export default class AuthSignupController extends BaseController {
  static targets = [
    "form",
    "email",
    "password",
    "passwordConfirm",
    "termsAccepted",
    "submit",
    "emailError",
    "passwordError",
    "termsError",
    "error",
    "rule",
    "mismatch",
  ];

  connect() {
    this.clearAllErrors();
    this.updateRules();
    this.updateSubmitState();

    // EPIC-038 / 38.6 §2.K — a purged demo account is offered a same-email
    // re-registration: the login page CTA redirects here with ?email=<addr>.
    // Pre-fill the field and focus the password (the email is already known).
    const prefillEmail = new URLSearchParams(window.location.search).get("email");
    if (prefillEmail && this.hasEmailTarget) {
      this.emailTarget.value = prefillEmail;
    }

    if (this.hasEmailTarget) {
      const focusField = prefillEmail && this.hasPasswordTarget
        ? this.passwordTarget
        : this.emailTarget;
      queueMicrotask(() => focusField.focus());
    }

    // A prefilled email changes form validity — recompute the submit state.
    this.updateSubmitState();

    // EPIC-038 / 38.4 §2.K — funnel de conversion : 1er affichage de la
    // page d'inscription.
    this.trackSignupStarted();
  }

  // ---------------------------------------------------------------------------
  // EPIC-038 / 38.4 — tracker de conversion (funnel signup)
  // ---------------------------------------------------------------------------

  /**
   * Émet `signup_started` une seule fois par visite. Dédup `sessionStorage`
   * pour ne pas ré-émettre au retour navigateur (rechargement complet).
   */
  trackSignupStarted() {
    try {
      const key = "nm-tracked-signup_started";
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch (_e) {
      // sessionStorage indisponible — on émet quand même (sans dédup).
    }
    window.NotamicroTracker?.track("signup_started");
  }

  async submit(event) {
    event.preventDefault();

    this.clearAllErrors();

    const email = this.emailTarget.value.trim();
    const password = this.passwordTarget.value;
    const passwordConfirm = this.hasPasswordConfirmTarget ? this.passwordConfirmTarget.value : "";
    const termsAccepted = this.hasTermsAcceptedTarget && this.termsAcceptedTarget.checked;

    // Client-side guard — keep server as the source of truth for validation
    // but skip the round-trip on obvious gaps.
    let firstInvalid = null;
    if (!email || !this.isValidEmail(email)) {
      this.showFieldError("emailError", "Adresse email invalide.");
      firstInvalid = firstInvalid ?? this.emailTarget;
    }
    const violations = validatePassword(password);
    if (violations.length > 0) {
      this.showFieldError("passwordError", formatViolations(violations));
      firstInvalid = firstInvalid ?? this.passwordTarget;
    } else if (password !== passwordConfirm) {
      this.showFieldError("passwordError", "Les deux mots de passe ne correspondent pas.");
      firstInvalid = firstInvalid ?? (this.hasPasswordConfirmTarget ? this.passwordConfirmTarget : this.passwordTarget);
    }
    if (!termsAccepted) {
      this.showFieldError("termsError", "Vous devez accepter les CGU pour continuer.");
      firstInvalid = firstInvalid ?? this.termsAcceptedTarget;
    }
    if (firstInvalid) {
      firstInvalid.focus();
      return;
    }

    this.setBusy(true);

    try {
      const result = await api.signup({
        email,
        password,
        termsAccepted: true,
        termsVersion: TERMS_VERSION,
      });
      // EPIC-038 / 38.4 — funnel : inscription aboutie. `organizationId`
      // n'est inclus que si la réponse l'expose (la 201 anti-énumération
      // D8 est volontairement uniforme → contexte le plus souvent vide).
      const context = result?.organizationId ? { organizationId: result.organizationId } : {};
      window.NotamicroTracker?.track("signup_completed", context);
      const params = new URLSearchParams({ email });
      window.location.assign(`/inscription-succes.html?${params.toString()}`);
    } catch (err) {
      this.handleSignupError(err);
    } finally {
      this.setBusy(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Error handling
  // ---------------------------------------------------------------------------

  handleSignupError(err) {
    const status = err instanceof APIError ? err.status : 0;

    if (status === 422) {
      const fields = err?.data?.error?.details?.fields ?? {};
      this.showFieldErrors(fields);
      return;
    }

    if (status === 400) {
      this.showFormError("Requête invalide. Réessayez.");
      return;
    }

    if (status === 0 || status === 408 || status >= 500) {
      this.showFormError("Réseau injoignable. Réessayez.");
      return;
    }

    this.showFormError("Une erreur est survenue. Réessayez.");
  }

  showFieldErrors(fields) {
    let firstFocused = false;
    if (Array.isArray(fields.email) && fields.email.length > 0) {
      this.showFieldError("emailError", this.translateViolations(fields.email));
      if (!firstFocused) {
        this.emailTarget.focus();
        firstFocused = true;
      }
    }
    if (Array.isArray(fields.password) && fields.password.length > 0) {
      this.showFieldError("passwordError", this.translateViolations(fields.password));
      if (!firstFocused) {
        this.passwordTarget.focus();
        firstFocused = true;
      }
    }
    if (Array.isArray(fields.termsAccepted) && fields.termsAccepted.length > 0) {
      this.showFieldError("termsError", this.translateViolations(fields.termsAccepted));
      if (!firstFocused && this.hasTermsAcceptedTarget) {
        this.termsAcceptedTarget.focus();
        firstFocused = true;
      }
    }
    if (Array.isArray(fields.termsVersion) && fields.termsVersion.length > 0) {
      this.showFormError(this.translateViolations(fields.termsVersion));
    }
  }

  translateViolations(codes) {
    return codes
      .map((code) => VIOLATION_LABELS[code] || RULE_LABELS[code] || code)
      .join(" ");
  }

  // ---------------------------------------------------------------------------
  // Live rule + mismatch indicators
  // ---------------------------------------------------------------------------

  updateRules() {
    if (this.hasPasswordTarget && this.hasRuleTarget) {
      const checks = checkRules(this.passwordTarget.value);
      for (const li of this.ruleTargets) {
        const ok = checks[li.dataset.rule] === true;
        li.classList.toggle("is-ok", ok);
        const mark = li.querySelector(".nm-check");
        if (mark) mark.textContent = ok ? "✓" : "○";
      }
    }
    // Le message d'erreur posté au submit doit disparaître dès que
    // l'utilisateur corrige son mot de passe.
    this.clearFieldError("passwordError");
    this.updateSubmitState();
  }

  onEmailInput() {
    this.clearFieldError("emailError");
    this.updateSubmitState();
  }

  updateMismatch() {
    if (this.hasMismatchTarget) {
      const confirm = this.passwordConfirmTarget.value;
      this.mismatchTarget.hidden =
        confirm.length === 0 || this.passwordTarget.value === confirm;
    }
    this.updateSubmitState();
  }

  /**
   * Active le bouton de création quand email + mot de passe sont valides.
   * On exclut volontairement la case CGU : elle n'est pas signalée comme
   * obligatoire, donc griser le bouton dessus serait opaque. Son acceptation
   * reste vérifiée au submit (message d'erreur dédié).
   */
  updateSubmitState() {
    if (!this.hasSubmitTarget) return;
    this.submitTarget.disabled = !this.isFormValid();
  }

  isFormValid() {
    const email = this.hasEmailTarget ? this.emailTarget.value.trim() : "";
    if (!email || !this.isValidEmail(email)) return false;

    const password = this.hasPasswordTarget ? this.passwordTarget.value : "";
    if (validatePassword(password).length > 0) return false;

    const passwordConfirm = this.hasPasswordConfirmTarget ? this.passwordConfirmTarget.value : "";
    if (password !== passwordConfirm) return false;

    return true;
  }

  showFieldError(targetName, message) {
    const target = this[`${targetName}Target`];
    if (!target) return;
    target.textContent = message;
    target.hidden = false;
  }

  showFormError(message) {
    if (!this.hasErrorTarget) return;
    const fragment = this.cloneTemplate("tpl-form-error");
    this.fillSlots(fragment, { message });
    this.errorTarget.replaceChildren(fragment);
    this.errorTarget.hidden = false;
  }

  clearFieldError(targetName) {
    const target = this[`${targetName}Target`];
    if (!target || target.hidden) return;
    target.textContent = "";
    target.hidden = true;
  }

  clearAllErrors() {
    for (const name of ["emailError", "passwordError", "termsError"]) {
      const target = this[`${name}Target`];
      if (target) {
        target.textContent = "";
        target.hidden = true;
      }
    }
    if (this.hasErrorTarget) {
      this.errorTarget.replaceChildren();
      this.errorTarget.hidden = true;
    }
  }

  setBusy(busy) {
    if (!this.hasSubmitTarget) return;
    this.submitTarget.setAttribute("aria-busy", busy ? "true" : "false");
    if (busy) {
      this.submitTarget.disabled = true;
    } else {
      // Restaure l'état réel (et non un simple ré-activage) après la requête.
      this.updateSubmitState();
    }
  }
}
